// Response normalisation for the organigram graph endpoint. Kept in its own module, free of
// Angular and service imports, so it can be unit-tested: importing OrganigramApiService pulls in
// CustomerdatabaseService and its import cycle, which crashes the Karma bootstrap.

export type OrganigramNodeTypeName = 'human' | 'agent' | 'team' | 'department' | 'division' | 'trigger' | 'pipeline';

interface NormalizableNode {
  type?: OrganigramNodeTypeName;
  team_type?: OrganigramNodeTypeName | null;
}

interface NormalizableGraph {
  nodes?: NormalizableNode[];
}

// The backend sends grouping nodes as type 'team' plus a team_type naming the layer
// ('department', 'division', or 'team' itself). team_type REFINES type, it never replaces it:
// human and agent nodes carry team_type: null, so assigning it unconditionally set their type to
// undefined and every `node.type === 'human' | 'agent'` check downstream silently stopped matching.
export function applyTeamType<T extends NormalizableGraph>(graph: T): T {
  for (const node of graph?.nodes || []) {
    if (node.team_type) { node.type = node.team_type; }
  }
  return graph;
}

interface ParentNameNode {
  id?: string;
  name?: string;
}

// Graph edges are directed parent -> child.
interface ParentNameEdge {
  source?: string;
  target?: string;
}

interface ParentNameGraph {
  nodes?: ParentNameNode[];
  edges?: ParentNameEdge[];
}

// Child node id -> the name of its parent, resolved from the graph edges. The chat dock shows this
// as a row subtitle, so two agents that share a name stay tellable apart by the team they sit under.
// A node may carry several parents (see OrganigramApiService.addParent) - the first edge wins.
export function buildParentNames(graph: ParentNameGraph): Map<string, string> {
  const names = new Map<string, string>();
  for (const node of graph?.nodes || []) {
    if (node?.id) { names.set(node.id, node.name || ''); }
  }
  const parents = new Map<string, string>();
  for (const edge of graph?.edges || []) {
    if (!edge?.source || !edge?.target || edge.source === edge.target || parents.has(edge.target)) { continue; }
    const name = names.get(edge.source);
    if (name) { parents.set(edge.target, name); }
  }
  return parents;
}
